// Инфографика: каталог шаблонов и генерация картинок.
// Перенос с приложения (ingame_admin/src/actions/socialPictures.js).
// Картинки рисует сервер: isHtml: true -> HTML для превью, false -> base64 PNG.

import axios from 'axios';
import { BASE_URL, API_URLS } from '../constants/config';
import { refreshTokens, logOut } from './usersActions';
import { checkIfTokenExpired, getTokensFromStore } from '../utilities/tokens';

const authorizedTokens = async (dispatch) => {
  if (checkIfTokenExpired()) {
    await dispatch(refreshTokens());
  }
  return getTokensFromStore();
};

const authHeaders = (tokens) => ({
  headers: {
    "Authorization": "Bearer " + tokens.access_token,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const handleAuthError = (dispatch, error) => {
  if (error.response && error.response.status === 401) {
    dispatch(logOut());
  }
  throw error;
};

// Каталог шаблонов. type — из SOCIAL_PATTERN_TYPES, 0 = все
export function fetchSocialPatterns(type, offset, limit) {
  return async dispatch => {
    const tokens = await authorizedTokens(dispatch);
    return axios.post(
      BASE_URL.url + API_URLS.getAllSocialPatterns,
      { type, offset, limit },
      authHeaders(tokens),
    ).then(res => res.data)
      .catch(error => handleAuthError(dispatch, error));
  };
}

// Палитра цветов клиента для шаблонов с настраиваемыми цветами
export function fetchSocialColors() {
  return async dispatch => {
    const tokens = await authorizedTokens(dispatch);
    return axios.post(BASE_URL.url + API_URLS.getSocialColors, {}, authHeaders(tokens))
      .then(res => res.data)
      .catch(error => handleAuthError(dispatch, error));
  };
}

// Тело запроса генерации. Набор полей повторяет приложение один в один —
// сервер ждёт именно эти имена (socialPictures.getSocialPic).
const buildPictureBody = (params, isHtml) => ({
  leagueId: params.league_id,
  date: params.date,
  partnersEnable: params.partnersEnable,
  vk: params.vk,
  instagram: params.inst,
  player_id: params.player_id,
  team_id: params.team_id,
  stage_id: params.stage_id,
  match_id: params.match_id,
  season_id: params.season_id,
  groups: params.groups,
  event_type: params.event_type,
  isHtml,
  players: params.players,
  scheme: params.scheme,
  newsData: params.newsData,
  playersTeamImages: params.playersTeamImages,
  activeColor: params.activeColor,
  customBg: params.customBg,
  activeHeader: params.activeHeader,
  activeBottom: params.activeBottom,
  activeMatches: params.activeMatches,
  goalTimeEnabled: params.goalTimeEnabled,
  isGroupEvent: params.isGroupEvent,
  customBgTwo: params.customBgTwo,
  activeMainTeam: params.activeMainTeam,
  activeSubTeam: params.activeSubTeam,
  activeCoaches: params.activeCoaches,
  label: params.label,
  videoEnabled: params.videoEnabled,
  customBgThree: params.customBgThree,
  customBgFour: params.customBgFour,
  customBgFive: params.customBgFive,
  activeColors: params.activeColors,
  mvpLabel_type: params.mvpLabel_type,
  activePlayoffMatches: params.activePlayoffMatches,
  includeTourTeam: params.includeTourTeam,
});

// Превью. Каждое изменение параметра шлёт новый запрос, предыдущий отменяем —
// иначе ответы приходят вперемешку и превью «прыгает».
// На сайте axios 0.21, где AbortController ещё не поддерживается,
// поэтому отмена через CancelToken — как в приложении.
export const createPreviewCancelSource = () => axios.CancelToken.source();

// isHtml: true — как в приложении. HTML это запрос в базу и склейка строки,
// а PNG сервер рисует headless-браузером: секунды и сотня мегабайт на рендер.
// Живое превью дёргается на каждое изменение параметра, поэтому гонять через
// него тяжёлый рендер нельзя. Точный PNG запрашивается отдельно и по кнопке —
// см. fetchPictureBase64.
// Без таймаута зависший на сервере рендер крутит спиннер бесконечно и не
// объясняет, что произошло. Лучше оборвать и показать ошибку
export const PREVIEW_TIMEOUT_MS = 60000;
export const RENDER_TIMEOUT_MS = 180000;

export function fetchPicturePreview(url, params, cancelSource) {
  return async dispatch => {
    const tokens = await authorizedTokens(dispatch);
    return axios.post(BASE_URL.url + url, buildPictureBody(params, true), {
      ...authHeaders(tokens),
      timeout: PREVIEW_TIMEOUT_MS,
      cancelToken: cancelSource ? cancelSource.token : undefined,
    }).then(res => res.data)
      .catch(error => {
        // Отменённый запрос — не ошибка: пользователь просто поменял параметр
        if (axios.isCancel(error)) return null;
        return handleAuthError(dispatch, error);
      });
  };
}

// Финальная картинка на скачивание — тот же рут, но isHtml: false
export function fetchPictureBase64(url, params) {
  return async dispatch => {
    const tokens = await authorizedTokens(dispatch);
    return axios.post(BASE_URL.url + url, buildPictureBody(params, false), {
      ...authHeaders(tokens),
      timeout: RENDER_TIMEOUT_MS,
    })
      .then(res => res.data)
      .catch(error => handleAuthError(dispatch, error));
  };
}


// Лиги админа с сезонами — тот же рут, что в приложении (getAdminLeagues).
// Отдаёт championat_name, league_id, league_name, league_image и seasons
export function fetchAdminLeaguesWithSeasons() {
  return async dispatch => {
    const tokens = await authorizedTokens(dispatch);
    return axios.post(BASE_URL.url + '/leagues/1/adminLeagues', {}, authHeaders(tokens))
      .then(res => res.data)
      .catch(error => handleAuthError(dispatch, error));
  };
}

// Туры (стадии) лиги в сезоне
export function fetchLeagueStages(leagueId, seasonId) {
  return async dispatch => {
    const tokens = await authorizedTokens(dispatch);
    return axios.post(
      BASE_URL.url + `/leagues/${leagueId}/getLeagueStages`,
      { league_id: leagueId, season_id: seasonId },
      authHeaders(tokens),
    ).then(res => res.data)
      .catch(error => handleAuthError(dispatch, error));
  };
}


// Матчи тура. Рут публичный, как и в приложении (fetchLeagueMatches)
export function fetchStageMatches(leagueId, seasonId, stageId) {
  return async dispatch => {
    return axios.post(
      BASE_URL.url + API_URLS.getLeagueMatches.replace(':leagueId', leagueId),
      { season_id: seasonId, stage: stageId },
      { headers: { Accept: 'application/json', 'Content-Type': 'application/json' } },
    ).then(res => res.data);
  };
}

// Матчи на дату — для шаблонов, где тур не выбирается.
// Рут ждёт тот же набор полей, что шлёт приложение (MatchChoose через
// fetchLeagueMatches): без client_id он отвечает 500, а season_id и stage
// должны прийти явными false, а не отсутствовать
export function fetchMatchesByDate(date, clientId) {
  return async dispatch => {
    return axios.post(BASE_URL.url + API_URLS.getMatchesByDate, {
      season_id: false,
      stage: false,
      date,
      client_id: clientId || false,
    }, {
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    }).then(res => res.data);
  };
}

// Игроки лиги в сезоне. search — по фамилии и имени
export function fetchLeaguePlayers(leagueId, seasonId, search, teamId) {
  return async dispatch => {
    const tokens = await authorizedTokens(dispatch);
    const parts = (search || '').trim().split(' ').filter(part => part.length > 0);
    return axios.post(BASE_URL.url + API_URLS.getLeaguePlayers, {
      league_id: leagueId,
      season_id: seasonId,
      team_id: teamId,
      search: (search || '').trim(),
      family: parts[0] || undefined,
      name: parts[1] || undefined,
    }, authHeaders(tokens))
      .then(res => res.data)
      .catch(error => handleAuthError(dispatch, error));
  };
}

// Группы сезона. withIds — рут, который отдаёт ещё и id группы
export function fetchLeagueGroups(leagueId, seasonId, withIds) {
  return async dispatch => {
    const tokens = await authorizedTokens(dispatch);
    const path = withIds ? API_URLS.getLeagueGroupsWithIds : API_URLS.getLeagueGroupsList;
    return axios.post(
      BASE_URL.url + path.replace(':leagueId', leagueId),
      { season_id: seasonId },
      authHeaders(tokens),
    ).then(res => res.data)
      .catch(error => handleAuthError(dispatch, error));
  };
}
